import { MergeFormConfig } from "@/types/merge";
import { Candidate } from "@/types/candidate";

// Export the LinkedIn profile data type
export interface LinkedInProfileData {
	participant: {
		object?: string;
		provider?: string;
		provider_id?: string;
		public_identifier?: string;
		member_urn?: string;
		first_name?: string;
		last_name?: string;
		headline?: string;
		primary_locale?: {
			country?: string;
			language?: string;
		};
		is_open_profile?: boolean;
		is_premium?: boolean;
		is_influencer?: boolean;
		is_creator?: boolean;
		is_relationship?: boolean;
		network_distance?: string;
		is_self?: boolean;
		websites?: string[];
		follower_count?: number;
		connections_count?: number;
		location?: string;
		contact_info?: {
			emails?: string[];
			phones?: string[];
		};
		birthdate?: {
			month?: number;
			day?: number;
		};
		profile_picture_url?: string;
		profile_picture_url_large?: string;
		background_picture_url?: string;
		education?: Array<{
			degree?: string;
			school_id?: string;
			school?: string;
			start?: string;
			end?: string;
		}>;
		work_experience?: Array<{
			company_id?: string;
			company?: string;
			position?: string;
			location?: string;
			description?: string;
			skills?: string[];
			start?: string;
			end?: string;
		}>;
		skills?: Array<{
			name?: string;
			endorsement_count?: number;
			insights?: string[];
			endorsement_id?: number;
			endorsed?: boolean;
		}>;
		languages?: Array<{
			name?: string;
			proficiency?: string;
		}>;
		certifications?: Array<{
			name?: string;
			organization?: string;
			url?: string;
		}>;
		summary?: string;
		volunteering_experience?: any[];
		projects?: any[];
		recommendations?: {
			received?: any[];
			given?: any[];
		};
		shared_connections_count?: number;
		creator_website?: {
			url?: string;
			description?: string;
		};
	};
}

// Helper function to extract skills from LinkedIn work experience and skills
function extractLinkedInSkills(profileData: LinkedInProfileData): string[] {
	const skills: string[] = [];
	
	// Extract from skills section
	if (profileData.participant?.skills) {
		skills.push(...profileData.participant.skills.map(skill => skill.name || '').filter(Boolean));
	}
	
	// Extract from work experience
	if (profileData.participant?.work_experience) {
		profileData.participant.work_experience.forEach(exp => {
			if (exp.skills) {
				skills.push(...exp.skills);
			}
		});
	}
	
	// Remove duplicates and empty strings
	return [...new Set(skills.filter(skill => skill.trim().length > 0))];
}

// Helper function to generate a summary from LinkedIn profile data
function generateLinkedInSummary(profileData: LinkedInProfileData): string {
	const participant = profileData.participant;
	if (!participant) return '';
	
	let summary = '';
	
	// Use existing summary if available
	if (participant.summary) {
		summary = participant.summary;
	} else {
		// Generate summary from other profile data
		const parts: string[] = [];
		
		if (participant.headline) {
			parts.push(participant.headline);
		}
		
		if (participant.work_experience && participant.work_experience.length > 0) {
			const currentJob = participant.work_experience[0];
			if (currentJob.position && currentJob.company) {
				parts.push(`Currently working as ${currentJob.position} at ${currentJob.company}`);
			}
		}
		
		if (participant.location) {
			parts.push(`Based in ${participant.location}`);
		}
		
		summary = parts.join('. ');
	}
	
	return summary;
}

// Helper function to convert LinkedIn profile to candidate format
export function convertLinkedInToCandidate(profileData: LinkedInProfileData, existingCandidate: Candidate): Candidate {
	const participant = profileData.participant;
	if (!participant) return existingCandidate;
	
	const linkedInCandidate: Candidate = {
		...existingCandidate,
		name: `${participant.first_name || ''} ${participant.last_name || ''}`.trim() || existingCandidate.name,
		function: participant.headline || existingCandidate.function,
		location: participant.location || existingCandidate.location,
		skills: extractLinkedInSkills(profileData),
		ai_synthesis: generateLinkedInSummary(profileData),
		linkedin_json: profileData,
		// Extract contact info if available
		email: participant.contact_info?.emails?.[0] || existingCandidate.email,
		phone: participant.contact_info?.phones?.[0] || existingCandidate.phone,
	};
	
	// Only update LinkedIn URL if it's not already set
	if (!existingCandidate.linkedin && participant.public_identifier) {
		linkedInCandidate.linkedin = `https://www.linkedin.com/in/${participant.public_identifier}`;
	}
	
	return linkedInCandidate;
}

// Configuration for merging LinkedIn profile with candidate data
export const linkedInCandidateMergeConfig: MergeFormConfig<Candidate> = {
	entityName: "candidates",
	excludeFields: ["id", "embedding", "embedding_specific", "organization_uuid", "created_at", "updated_at", "match_score", "linkedin_json"], // Hide linkedin_json from UI
	fields: [
		{
			name: "name",
			type: "text",
			label: "Full Name",
			mergeBehavior: "manual",
			required: true,
			description: "Candidate's full name"
		},
		{
			name: "email",
			type: "email",
			label: "Email",
			mergeBehavior: "manual",
			required: true,
			description: "Primary email address"
		},
		{
			name: "phone",
			type: "phone",
			label: "Phone",
			mergeBehavior: "manual",
			description: "Primary phone number"
		},
		{
			name: "function",
			type: "text",
			label: "Job Function/Headline",
			mergeBehavior: "manual",
			aiMergeEnabled: true,
			description: "Current job title or professional headline"
		},
		{
			name: "skills",
			type: "array",
			label: "Skills",
			mergeBehavior: "combine",
			description: "Professional skills and competencies"
		},
		{
			name: "location",
			type: "text",
			label: "Location",
			mergeBehavior: "manual",
			description: "Geographic location"
		},
		{
			name: "ai_synthesis",
			type: "text",
			label: "Professional Summary",
			mergeBehavior: "ai",
			aiMergeEnabled: true,
			description: "Professional summary or about section"
		},
		{
			name: "status",
			type: "select",
			label: "Status",
			mergeBehavior: "first",
			possibleValues: ["looking", "open", "unavailable", "hired"],
			description: "Current candidate status (keep existing value)"
		},
		{
			name: "manager",
			type: "text",
			label: "Manager",
			mergeBehavior: "first",
			description: "Assigned manager (keep existing value)"
		},
		{
			name: "linkedin",
			type: "url",
			label: "LinkedIn URL",
			mergeBehavior: "manual",
			description: "LinkedIn profile URL"
		}
	]
};